'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useSelector } from 'react-redux'
import { getWorkingHours } from '@/_service/common.service'
import {
  reservationService,
  useAvailableTablesList,
  useSingleReservation,
} from '@/_service/reservation'
import { IRedux } from '@/_store/types'
import {
  IOption,
  IReservationValues,
  IWorkingHours,
  IWorkingHoursResponse,
} from '@/_types'
import { useCustomLocale } from '@/shared/hooks/use-custom-locale'
import { toaster } from '@/components/ui/toaster'
import { ITableOption } from '../index.legacy'
import { ReservationFormViewProps } from './types'

const formatTime = (time: Dayjs | null) =>
  time && time?.isValid() ? time.format('HH:mm') : ''

const formatDate = (date: dayjs.ConfigType) =>
  date ? dayjs(date).format('YYYY-MM-DD') : ''

const buildTableLabel = (
  number: number,
  seatingCap: number,
  isAvailable: boolean,
  t: ReturnType<typeof useTranslations>
) =>
  `${t('table_stol')} ${number} (${t('seats_count')}: ${seatingCap})${
    !isAvailable ? ` ${t('reserved')}` : ''
  }`

export function useReservationForm(id?: string): ReservationFormViewProps {
  const router = useRouter()
  const t = useTranslations()
  const locale = useCustomLocale()
  const { user } = useSelector((state: IRedux) => state.auth)

  const isCreateMode = !id || id === 'create'
  const skipAvailabilityResetRef = useRef(false)
  const editDataLoadedRef = useRef(false)

  const [isSaving, setIsSaving] = useState(false)
  const [isEditable, setIsEditable] = useState(true)
  const [workHours, setWorkHours] = useState<IWorkingHours | null>(null)
  const [autoOpenPicker, setAutoOpenPicker] = useState<'start' | 'end' | ''>('')

  const [formData, setFormData] = useState<IReservationValues>({
    branch_id: null,
    table_id: null,
    customer_id: '',
    customer_name: '',
    phone_number: '',
    extra_phone_number: '',
    section_id: null,
    description: '',
    reserve_date: null,
    start_time: null,
    end_time: null,
    guest_count: '',
  })

  const reserveDate = formatDate(formData.reserve_date)
  const startTime = formatTime(formData.start_time)
  const endTime = formatTime(formData.end_time)

  const canFetchAvailability = Boolean(
    formData.branch_id?.value &&
      formData.reserve_date &&
      formData.start_time?.isValid() &&
      formData.end_time?.isValid()
  )

  const availabilityKey = [
    formData.branch_id?.value,
    reserveDate,
    startTime,
    endTime,
  ].join('|')

  const { data: reservationData } = useSingleReservation({
    id: id || '',
    props: {
      enabled: !!id && id !== 'create',
    },
  })

  const externalReserveId = reservationData?.external_reserve_id || ''

  const { data: workingHoursData } = useQuery<IWorkingHoursResponse>({
    queryKey: [
      'branch_working_hours_time',
      formData.branch_id?.value,
      reserveDate,
    ],
    queryFn: () =>
      getWorkingHours(formData.branch_id?.value, reserveDate).then(
        (res) => res?.data
      ),
    enabled: Boolean(formData.branch_id?.value && formData.reserve_date),
  })

  const { data: sectionsData, isLoading: isSectionsLoading } =
    useAvailableTablesList({
      params: {
        branch_id: formData.branch_id?.value,
        reserve_date: reserveDate,
        start_date: startTime,
        end_date: endTime,
        limit: 10,
        page: 1,
      },
      props: { enabled: canFetchAvailability },
    })

  const { data: tablesData, isLoading: isTablesLoading } =
    useAvailableTablesList({
      params: {
        branch_id: formData.branch_id?.value,
        reserve_date: reserveDate,
        start_date: startTime,
        end_date: endTime,
        section_id: formData.section_id?.value,
        limit: 30,
        page: 1,
      },
      props: {
        enabled: canFetchAvailability && Boolean(formData.section_id?.value),
      },
    })

  const sectionOptions: ITableOption[] = useMemo(() => {
    return (
      sectionsData?.sections
        ?.filter((section) => section.status)
        .map((section) => ({
          label: section.name?.[locale] ?? section.name?.ru ?? section.id,
          value: section.id,
        })) ?? []
    )
  }, [sectionsData, locale])

  const tableOptions: ITableOption[] = useMemo(() => {
    const selectedSection = tablesData?.sections?.find(
      (section) => section.id === formData.section_id?.value
    )

    return (
      selectedSection?.tables?.map((table) => ({
        label: buildTableLabel(
          table.number,
          table.seating_cap,
          table.is_available,
          t
        ),
        value: table.id,
        isDisabled: !table.is_available,
      })) ?? []
    )
  }, [tablesData, formData.section_id?.value, t])

  const createUpdateReservation = (values: IReservationValues) => {
    const body = {
      branch_id: values.branch_id?.value,
      customer_id: values.customer_id,
      description: values.description,
      start_time: formatTime(values.start_time),
      end_time: formatTime(values.end_time),
      extra_phone_number:
        values.extra_phone_number?.length === 12
          ? values.extra_phone_number
          : '',
      reserve_date: formatDate(values.reserve_date),
      section_id: values.section_id?.value,
      table_id: values.table_id?.value,
    }

    const request =
      id && id !== 'create'
        ? reservationService.update(id, body)
        : reservationService.create(body)

    request
      .then(() => {
        router.push('/orders?order_type=hall')
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (
      !formData.branch_id ||
      !formData.reserve_date ||
      !formData.start_time ||
      !formData.end_time ||
      !formData.section_id ||
      !formData.table_id
    ) {
      return
    }

    if (!formData.customer_id) {
      toaster.create({
        title: 'Вы не вошли в систему',
        description: 'Пожалуйста, войдите на веб-сайт для бронирования',
      })
      return
    }

    setIsSaving(true)
    createUpdateReservation(formData)
  }

  useEffect(() => {
    if (formData.reserve_date && workingHoursData) {
      setWorkHours(
        workingHoursData.working_hours?.[
          dayjs(formData.reserve_date).get('day')
        ] ?? null
      )
    }
  }, [workingHoursData, formData.reserve_date])

  useEffect(() => {
    if (!reservationData) return

    editDataLoadedRef.current = true
    skipAvailabilityResetRef.current = true

    const sectionLabel =
      reservationData.section?.name?.[locale] ??
      reservationData.section?.name?.ru ??
      reservationData.section_id

    setFormData({
      branch_id: {
        label: reservationData.branch_name,
        value: reservationData.branch_id,
      },
      table_id: {
        label: buildTableLabel(
          reservationData.table?.number ?? 0,
          reservationData.table?.seating_cap ?? 0,
          true,
          t
        ),
        value: reservationData.table_id || reservationData.table?.id,
      },
      section_id: {
        label: sectionLabel,
        value: reservationData.section_id,
      },
      customer_id: reservationData.customer_id,
      customer_name: reservationData.customer_name,
      phone_number: reservationData.customer_phone,
      extra_phone_number: reservationData.extra_phone_number || '',
      description: reservationData.description || '',
      reserve_date: reservationData.reserve_date
        ? dayjs(reservationData.reserve_date)
        : null,
      start_time: reservationData.start_time
        ? dayjs(reservationData.start_time, 'HH:mm')
        : null,
      end_time: reservationData.end_time
        ? dayjs(reservationData.end_time, 'HH:mm')
        : null,
      guest_count: String(reservationData.table?.seating_cap || ''),
    })

    if (
      reservationData.status === 'canceled' ||
      reservationData.status === 'finished'
    ) {
      setIsEditable(false)
    }
  }, [reservationData, locale, t])

  useEffect(() => {
    if (!user) return

    setFormData((prev) => ({
      ...prev,
      customer_name: user.name,
      phone_number: user.phone,
      customer_id: user.id,
    }))
  }, [user])

  useEffect(() => {
    if (!canFetchAvailability) return

    if (skipAvailabilityResetRef.current) {
      skipAvailabilityResetRef.current = false
      return
    }

    setFormData((prev) => ({
      ...prev,
      section_id: null,
      table_id: null,
    }))
  }, [availabilityKey, canFetchAvailability])

  useEffect(() => {
    if (!canFetchAvailability || !sectionsData?.sections?.length) return
    if (editDataLoadedRef.current) {
      editDataLoadedRef.current = false
      return
    }

    setFormData((prev) => {
      if (prev.section_id?.value) return prev

      const firstSection = sectionsData.sections.find(
        (section) => section.status && (section.tables?.length ?? 0) > 0
      )

      if (!firstSection) return prev

      return {
        ...prev,
        section_id: {
          label:
            firstSection.name?.[locale] ??
            firstSection.name?.ru ??
            firstSection.id,
          value: firstSection.id,
        },
        table_id: isCreateMode ? null : prev.table_id,
      }
    })
  }, [
    sectionsData,
    canFetchAvailability,
    locale,
    isCreateMode,
    availabilityKey,
  ])

  useEffect(() => {
    if (editDataLoadedRef.current) return

    setFormData((prev) => ({
      ...prev,
      table_id: isCreateMode ? null : prev.table_id,
    }))
  }, [formData.section_id?.value, isCreateMode])

  return {
    onSubmit,
    formData,
    setFormData,
    workHours,
    isEditable,
    isSaving,
    reservationId: id,
    externalId: externalReserveId,
    autoOpenPicker,
    setAutoOpenPicker,
    sectionOptions,
    tableOptions,
    canFetchAvailability,
    isSectionsLoading,
    isTablesLoading,
  }
}
